import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, LogIn } from "lucide-react";
import { BUSINESS } from "@/constants/business";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#faf7f2]/95 backdrop-blur-xl border-b border-[#e5ddd0] shadow-sm" : "bg-[#faf7f2]/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 flex items-center justify-between h-20 gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/brand/vila-logo-dark.png"
            alt="Vila Elevators & Escalators logo"
            className="h-9 w-auto group-hover:scale-105 transition-transform duration-300"
          />
          <span className="hidden sm:block text-[10px] text-[#6b6b6b] uppercase tracking-[0.2em] font-medium border-l border-[#e5ddd0] pl-3">
            Elevators &<br />Escalators
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${
                  isActive ? "text-[#b08d57]" : "text-[#4a4a4a] hover:text-[#1a1a1a]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="nav-phone"
            className="hidden 2xl:flex text-[#4a4a4a] hover:text-[#b08d57] text-sm items-center gap-2 transition-colors whitespace-nowrap"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-customer-login"
            className="border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-5 py-3 font-semibold uppercase text-xs tracking-wider transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <LogIn className="w-3.5 h-3.5" />
            Customer Login
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-6 py-3 font-semibold uppercase text-xs tracking-wider transition-colors whitespace-nowrap"
          >
            Get Quote
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#1a1a1a] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden bg-[#faf7f2] border-t border-[#e5ddd0]">
          <nav className="flex flex-col p-6 gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-lg font-display font-semibold py-2 ${
                    isActive ? "text-[#b08d57]" : "text-[#1a1a1a]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="text-[#4a4a4a] py-2 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <a
              href={BUSINESS.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-customer-login"
              onClick={() => setOpen(false)}
              className="border border-[#e5ddd0] text-[#1a1a1a] px-6 py-4 font-semibold uppercase text-center tracking-wider mt-2 flex items-center justify-center gap-2"
            >
              <LogIn className="w-4 h-4" /> Customer Login
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="bg-[#b08d57] text-white px-6 py-4 font-semibold uppercase text-center tracking-wider mt-2"
            >
              Get Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
